import React, { useEffect, useRef } from 'react';
import { Table, Empty } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import PageScaffold, { PageScaffoldButtonType } from '../../components/PageScaffold';
import TableSkeleton from '../../components/@shared/TableSkeleton';
import { useUsersPageController } from './controllers/useUsersPageController';
import { UsersPageModals } from './modals/UsersPageModals';
import { getUsersColumns } from './usersPage.columns';
import { useLocation, useNavigate } from 'react-router-dom';
import './UsersPage.css';
import { useContext } from 'react';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import CompanyPageHeader from '../company/CompanyPageHeader';
import { getCompanyTabPath } from '../company/useCompanyTabs';

export const USERS_PAGE_PATH = getCompanyTabPath('users');
export const USERS_PAGE_TITLE = 'Utilisateurs';

interface UsersPageProps { }

const UsersPage: React.FC<UsersPageProps> = () => {
  const { state, actions } = useUsersPageController();
  const { degradedSubscriptions } = useContext(AuthenticationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const handledLocationKeyRef = useRef<string | null>(null);
  const {
    currentPage,
    pageSize,
    users,
    totalUsers,
    isLoading,
    isReadOnly,
    isCompanyExternallyBilled,
  } = state;
  const { setSearchValue, setCurrentPage, setPageSize, openModal } = actions;

  const columns = getUsersColumns({ openModal, degradedSubscriptions, isCompanyExternallyBilled });

  const pageTitle = `${USERS_PAGE_TITLE} (${totalUsers})`;

  useEffect(() => {
    const state = location.state as { openModal?: 'ADD_USER' } | null;
    const modalToOpen = state?.openModal;
    if (modalToOpen === 'ADD_USER' && handledLocationKeyRef.current !== location.key) {
      handledLocationKeyRef.current = location.key;
      openModal('ADD_USER');
      // Clear router state to avoid reopening on any re-render.
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.key, location.pathname, location.state, navigate, openModal]);

  return (
    <PageScaffold
      header={<CompanyPageHeader />}
      title={pageTitle}
      subtitle=" "
      breadcrumbItems={[
        {
          title: ' ',
        },
      ]}
      controlsSettings={{
        searchLabel: 'Nom, prénom ou email',
        onSearch: (value) => setSearchValue(value),
        isLoading: false,
        onSearchCancel: () => setSearchValue(''),
        actionButtons: (!isReadOnly ? [{
            label: 'Ajouter un utilisateur',
            type: 'primary' as PageScaffoldButtonType,
            props: {
              icon: <PlusOutlined />,
              onClick: () => openModal('ADD_USER'),
            },
          }] : []),
      }}
    >
      {/* Table */}
      <div className="users-page__table">
        {isLoading ? (
          <TableSkeleton columns={columns} rows={pageSize} />
        ) : users.length === 0 ? (
          <Empty description="Aucun utilisateur trouvé" className="users-page__empty" />
        ) : (
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            pagination={{
              current: currentPage,
              total: totalUsers,
              pageSize: pageSize,
              onChange: setCurrentPage,
              onShowSizeChange: (current, newPageSize) => setPageSize(newPageSize),
              pageSizeOptions: [7, 28, 49, 70],
              showSizeChanger: true,
            }}
            rowClassName="table-row"
            onRow={(record) => ({
              onClick: () => openModal('VIEW_PROFILE', record),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </div>

      <UsersPageModals state={state} actions={actions} />
    </PageScaffold>
  );
};

export default UsersPage;
